/**
 * The screen-space post chain.
 *
 * Owns the five screen filters, their ordering, their animated uniforms, and
 * the memory budget. The scene is supposed to look expensive and calm — the
 * base state is a whisper of bloom, a warm vignette, enough grain to keep the
 * felt from banding, and a filmic grade over the top. Only bonuses flare, and
 * they flare through `setBloomBoost()` — the sink for `VFX`'s `onBloom`
 * callback, which owns the impulse and its decay. `flare()` is the
 * self-decaying equivalent for callers that have no easing loop of their own.
 *
 * ## The grade, and why it is last
 *
 * Bloom, aberration and vignette are all *lens* effects and grain is the film
 * stock; the grade is the print, so it goes after all of them. It is the one
 * pass that sees the whole frame as a photograph rather than as a set of
 * separately-authored correct elements — see `ColourGradeFilter` for what it
 * does and why the numbers are what they are.
 *
 * ## Selective defocus
 *
 * When a pile resolves, `setFocus()` points a shallow depth-of-field at it and
 * everything outside a soft radius eases into a 1–2px blur, so the eye goes
 * where the payoff is. The resolving pile itself stays perfectly sharp, the
 * ramp is eased in both directions, and a focus releases itself after `hold`
 * seconds even if nobody calls `clearFocus()` — a blur that outlives its
 * resolve would be blurring cards the player is trying to read, which is a
 * gameplay bug, not a cosmetic one.
 *
 * It costs nothing when it is not running: the blur passes only happen while
 * the strength is above zero, and the composite that mixes them in lives inside
 * the grade's shader rather than in a pass of its own. Under
 * `prefers-reduced-motion` it is off entirely.
 *
 * Memory note: Pixi allocates filter targets at the next power of two, so a
 * full-screen chain on a wide monitor at devicePixelRatio 2 would ask for
 * 8192x8192 targets. `setScreen()` caps the chain's resolution so the backing
 * textures stay within `maxTextureSize`, which keeps retina sharpness at
 * 1080p-class windows and degrades gracefully above that instead of eating a
 * gigabyte of VRAM.
 */

import type { Filter } from 'pixi.js';

import {
  BloomFilter,
  CardShield,
  ChromaticAberrationFilter,
  FilmGrainFilter,
  VignetteFilter,
} from '@/pixi/filters/screen';
import type {
  BloomFilterOptions,
  ChromaticAberrationFilterOptions,
  FilmGrainFilterOptions,
  VignetteFilterOptions,
} from '@/pixi/filters/screen';
// Imported from the module rather than the barrel so this file owns no edit to
// `filters/screen/index.ts`; add it there too if the barrel is convenient.
import { ColourGradeFilter } from '@/pixi/filters/screen/ColourGradeFilter';
import type { ColourGradeFilterOptions } from '@/pixi/filters/screen/ColourGradeFilter';
import { CARD, DESIGN, PILES, PILE_MAX_CARDS, pilePosition } from '@/pixi/layout';

export interface DefocusOptions {
  /**
   * Blur radius in **design units**, converted to device pixels on every
   * resize. The brief is 1–2; past that it stops reading as a lens.
   */
  blur?: number;
  /** Width of the sharp-to-blurred falloff, as a fraction of the focus radius. */
  feather?: number;
  /** Default strength for `setFocus()` / `focusPile()`. 0..1. */
  strength?: number;
  /**
   * Seconds a focus holds before releasing itself. `Infinity` waits for
   * `clearFocus()`, which is only safe if the caller cannot possibly miss it.
   */
  hold?: number;
  /** Ease-in rate, per second. Higher is snappier. */
  attack?: number;
  /** Ease-out rate, per second. */
  release?: number;
  /** Master switch. Forced off under `prefers-reduced-motion`. */
  enabled?: boolean;
}

export interface PostFXOptions {
  bloom?: BloomFilterOptions;
  aberration?: ChromaticAberrationFilterOptions;
  vignette?: VignetteFilterOptions;
  grain?: FilmGrainFilterOptions;
  grade?: ColourGradeFilterOptions;
  defocus?: DefocusOptions;
  /** Resting bloom level the chain always eases back to. */
  bloomBase?: number;
  /** Extra bloom added at a full 1.0 boost. */
  bloomGain?: number;
  /** Exponential decay rate of a `flare()`, per second. Higher = snappier. */
  bloomDecay?: number;
  /**
   * Base blur radius of the bloom, in **design units**. Converted to device
   * pixels on every resize so a flare is the same size relative to the table
   * on a phone and on a 4K monitor.
   */
  bloomSpread?: number;
  /** Largest filter target edge, in device pixels, before resolution is capped. */
  maxTextureSize?: number;
  /**
   * Override the `prefers-reduced-motion` query. Motion-driven effects (today:
   * the defocus) are disabled when this is true.
   */
  reducedMotion?: boolean;
}

const DEFAULT_BLOOM_BASE = 0.45;
const DEFAULT_BLOOM_GAIN = 1.15;
const DEFAULT_BLOOM_DECAY = 3.4;
const DEFAULT_BLOOM_SPREAD = 5;
const DEFAULT_MAX_TEXTURE = 4096;

/** Defocus blur radius in design units. Two pixels at 1080p is the ceiling. */
const DEFAULT_DEFOCUS_BLUR = 1.6;
/** Falloff width as a fraction of the sharp radius. Wide, so no edge is visible. */
const DEFAULT_DEFOCUS_FEATHER = 0.62;
/** How far the surround goes toward the blurred frame at full ramp. */
const DEFAULT_FOCUS_STRENGTH = 0.85;
/**
 * Seconds a focus holds before it releases itself. Sized to the payoff — the
 * clear and bust timelines in `@/core/tokens` are 0.55 and 0.6 — plus the
 * settle after them.
 */
const DEFAULT_FOCUS_HOLD = 0.75;
/** Ease rates, per second. Attack is quicker than release: snap to it, drift off it. */
const DEFAULT_FOCUS_ATTACK = 9;
const DEFAULT_FOCUS_RELEASE = 4.5;
/** Slack around a pile's own bounds, so its total badge stays sharp too. */
const FOCUS_PILE_MARGIN = 1.2;

/** Longest plausible frame we will integrate in one step. */
const MAX_STEP_SECONDS = 1 / 15;

/** Below this a decaying boost snaps to zero so the chain reaches a real rest. */
const BOOST_EPSILON = 0.002;

/** Below this a releasing focus snaps to zero, which switches the pass off. */
const FOCUS_EPSILON = 0.003;

/** True when the OS asks for less movement. Safe on a server, where there is no matchMedia. */
function prefersReducedMotion(): boolean {
  const query = globalThis.matchMedia;
  if (typeof query !== 'function') return false;
  try {
    return query.call(globalThis, '(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export class PostFX {
  readonly bloom: BloomFilter;
  readonly aberration: ChromaticAberrationFilter;
  /** Resting lens fringing, restored whenever the boost falls back to 0. */
  #aberrationBase = 0;
  /**
   * How much fringing a full bloom boost adds, on top of the resting value.
   *
   * Over three times the resting value at boost 1 — a 6x streak, or a perfect
   * game. The first pass at this added about a pixel at the corners, which is
   * below what anybody notices on a frame that is also blooming and shaking;
   * fringing has to be visible to be a channel at all.
   */
  aberrationGain = 0.0075;
  readonly vignette: VignetteFilter;
  readonly grain: FilmGrainFilter;
  readonly grade: ColourGradeFilter;

  /**
   * The chain, in application order. Assign straight to `container.filters`.
   * Filmic order: lens glow, then lens dispersion, then lens falloff, then the
   * film stock the whole thing is exposed onto, then the print it is graded to.
   */
  readonly filters: Filter[];

  /**
   * Keeps bloom and aberration off the card pixels (see `CardShield`).
   *
   * `cardFilters` goes on the group the card layer lives in — the chain above
   * cannot be told where the cards are unless something at the cards says so.
   * The capture is switched with the two effects it serves, so at `low` the
   * group has no filter at all and costs nothing.
   */
  readonly shield = new CardShield();
  readonly cardFilters: Filter[] = [this.shield.capture];

  /** Resting bloom level. Everything decays back to this. */
  bloomBase: number;
  /** How much bloom a full 1.0 boost adds on top of `bloomBase`. */
  bloomGain: number;
  /** Exponential decay rate of a `flare()`, per second. */
  bloomDecay: number;
  /**
   * When true, `update()` owns `bloom.intensity`. Set false if something else
   * (a gsap tween on `bloomIntensity`, say) wants to drive it directly.
   */
  autoBloom = true;

  #elapsed = 0;
  /** 0..1 additive boost above the resting level. */
  #boost = 0;
  /** True when `flare()` set the boost, so `update()` should ease it away. */
  #boostDecays = false;
  #bloomSpreadDesign: number;
  #maxTextureSize: number;
  #resolutionCap: number | 'inherit' = 'inherit';
  #rendererResolution = 1;
  #designScale = 1;

  /** Whether the chain as a whole is switched on. Gates the grade's auto-skip. */
  #chainEnabled = true;

  #defocusBlurDesign: number;
  #focusFeather: number;
  #focusStrengthDefault: number;
  #focusHoldDefault: number;
  #focusAttack: number;
  #focusRelease: number;
  #defocusWanted: boolean;
  #defocusQuality = true;
  #reducedMotion: boolean;

  /** Eased state, all in design units except the 0..1 strength. */
  #focus = 0;
  #focusTarget = 0;
  #focusHold = 0;
  #focusX = DESIGN.width / 2;
  #focusY = DESIGN.height / 2;
  #focusRadius = DESIGN.height / 2;
  #targetX = DESIGN.width / 2;
  #targetY = DESIGN.height / 2;
  #targetRadius = DESIGN.height / 2;

  constructor(options: PostFXOptions = {}) {
    this.bloom = new BloomFilter(options.bloom);
    this.aberration = new ChromaticAberrationFilter(options.aberration);
    this.vignette = new VignetteFilter(options.vignette);
    this.grain = new FilmGrainFilter(options.grain);
    this.grade = new ColourGradeFilter(options.grade);

    this.filters = [this.bloom, this.aberration, this.vignette, this.grain, this.grade];
    this.bloom.shield = this.shield;
    this.aberration.shield = this.shield;

    // Captured before anything kicks it, so the kick always returns here.
    this.#aberrationBase = this.aberration.strength;
    this.bloomBase = options.bloomBase ?? options.bloom?.intensity ?? DEFAULT_BLOOM_BASE;
    this.bloomGain = options.bloomGain ?? DEFAULT_BLOOM_GAIN;
    this.bloomDecay = options.bloomDecay ?? DEFAULT_BLOOM_DECAY;
    this.#bloomSpreadDesign = options.bloomSpread ?? DEFAULT_BLOOM_SPREAD;
    this.#maxTextureSize = options.maxTextureSize ?? DEFAULT_MAX_TEXTURE;

    const defocus = options.defocus ?? {};
    this.#defocusBlurDesign = defocus.blur ?? DEFAULT_DEFOCUS_BLUR;
    this.#focusFeather = defocus.feather ?? DEFAULT_DEFOCUS_FEATHER;
    this.#focusStrengthDefault = defocus.strength ?? DEFAULT_FOCUS_STRENGTH;
    this.#focusHoldDefault = defocus.hold ?? DEFAULT_FOCUS_HOLD;
    this.#focusAttack = defocus.attack ?? DEFAULT_FOCUS_ATTACK;
    this.#focusRelease = defocus.release ?? DEFAULT_FOCUS_RELEASE;
    this.#defocusWanted = defocus.enabled ?? true;
    this.#reducedMotion = options.reducedMotion ?? prefersReducedMotion();

    this.#applyBloom();
    this.#applyBloomSpread();
    this.#applyDefocusBlur();
    this.#applyFocus();
  }

  /**
   * The hook `VFX` drives through its `onBloom` callback.
   *
   * `VFX` owns one scalar for the whole celebration layer and eases it back to
   * zero itself, so this deliberately does **not** decay: it just maps 0..1
   * onto `bloomBase .. bloomBase + bloomGain`. Two modules easing the same
   * number would double the falloff and make a big win read as a blink.
   *
   * ```ts
   * const vfx = new VFX({ onBloom: (boost) => postfx.setBloomBoost(boost) });
   * ```
   */
  setBloomBoost(boost: number): void {
    this.#boost = Math.min(Math.max(boost, 0), 1);
    this.#boostDecays = false;
    this.#applyBloom();
  }

  /** Current 0..1 boost above the resting level. */
  get bloomBoost(): number {
    return this.#boost;
  }

  /**
   * Self-decaying kick, for callers that do not already own an easing loop.
   * Prefer {@link setBloomBoost} when `VFX` is wired up.
   *
   * @param boost - 0..1, how far up to jump.
   */
  flare(boost = 1): void {
    this.#boost = Math.max(this.#boost, Math.min(Math.max(boost, 0), 1));
    this.#boostDecays = true;
    this.#applyBloom();
  }

  /** Current bloom strength. Safe to tween — set `autoBloom = false` first. */
  get bloomIntensity(): number {
    return this.bloom.intensity;
  }
  set bloomIntensity(value: number) {
    this.bloom.intensity = value;
  }

  /**
   * Strength of the final grade, 0..1. 0 is a true early-out: the shader
   * returns before any of the tone maths, and if no defocus is running the pass
   * switches itself off entirely on the next `update()`.
   */
  get gradeIntensity(): number {
    return this.grade.intensity;
  }
  set gradeIntensity(value: number) {
    this.grade.intensity = value;
    this.#syncGrade();
  }

  /* ---------------------------------------------------------------- defocus */

  /**
   * Point the shallow depth-of-field at a place on the table. Everything
   * outside `radius` eases into a 1–2px blur; everything inside stays sharp.
   *
   * Gate this on resolve state only. It must never be used to emphasise
   * something while the player is still choosing a pile — a blurred card is an
   * unreadable card.
   *
   * @param x - centre, in design units.
   * @param y - centre, in design units.
   * @param radius - design-unit radius that stays perfectly sharp.
   * @param strength - 0..1, how far the surround goes toward the blur.
   * @param hold - seconds before the focus releases itself. `Infinity` to wait
   *   for {@link clearFocus}.
   */
  setFocus(
    x: number,
    y: number,
    radius: number,
    strength: number = this.#focusStrengthDefault,
    hold: number = this.#focusHoldDefault,
  ): void {
    if (!this.defocusEnabled) return;

    const level = Math.min(Math.max(strength, 0), 1);
    if (level <= 0) {
      this.clearFocus();
      return;
    }

    this.#targetX = x;
    this.#targetY = y;
    this.#targetRadius = Math.max(1, radius);

    // Nothing is on screen yet, so place the focus rather than sliding it in
    // from wherever the last resolve happened to be.
    if (this.#focus <= FOCUS_EPSILON) {
      this.#focusX = this.#targetX;
      this.#focusY = this.#targetY;
      this.#focusRadius = this.#targetRadius;
    }

    this.#focusTarget = level;
    this.#focusHold = Math.max(0, hold);
    this.#applyFocus();
  }

  /**
   * Point the defocus at a pile, sized from `@/pixi/layout` so the whole fan
   * and its total badge stay sharp.
   *
   * ```ts
   * postfx.focusPile(resolution.pile, resolution.cards.length);
   * ```
   *
   * @param pile - pile index.
   * @param cardCount - how tall the fan is. Defaults to a full pile.
   */
  focusPile(
    pile: number,
    cardCount: number = PILE_MAX_CARDS,
    strength?: number,
    hold?: number,
  ): void {
    const first = pilePosition(pile);
    const cards = Math.min(Math.max(Math.round(cardCount), 1), PILE_MAX_CARDS);
    const fan = ((cards - 1) * PILES.fan) / 2;

    // The fan grows downward from the first card, so its centre drops with it.
    const centreY = first.y + fan;
    const radius = Math.hypot(CARD.width / 2, CARD.height / 2 + fan) * FOCUS_PILE_MARGIN;

    this.setFocus(first.x, centreY, radius, strength, hold);
  }

  /** Release the focus. The blur eases out rather than cutting. */
  clearFocus(): void {
    this.#focusTarget = 0;
    this.#focusHold = 0;
  }

  /** Current eased defocus strength, 0..1. 0 means no blur work is happening. */
  get focusStrength(): number {
    return this.#focus;
  }

  /**
   * Whether the defocus may run at all. False under `prefers-reduced-motion`,
   * at `low` quality, or when it has been switched off explicitly.
   */
  get defocusEnabled(): boolean {
    return this.#defocusWanted && this.#defocusQuality && !this.#reducedMotion;
  }
  set defocusEnabled(value: boolean) {
    this.#defocusWanted = value;
    if (!this.defocusEnabled) this.#killFocus();
  }

  /** Defocus blur radius in **design units**. 1–2 is the whole brief. */
  get defocusBlur(): number {
    return this.#defocusBlurDesign;
  }
  set defocusBlur(value: number) {
    this.#defocusBlurDesign = Math.max(0, value);
    this.#applyDefocusBlur();
  }

  /** Reflects `prefers-reduced-motion`, or whatever was passed in its place. */
  get reducedMotion(): boolean {
    return this.#reducedMotion;
  }
  set reducedMotion(value: boolean) {
    this.#reducedMotion = value;
    if (!this.defocusEnabled) this.#killFocus();
  }

  /* ----------------------------------------------------------------- frame */

  /**
   * Advance animated uniforms.
   *
   * @param dt - elapsed seconds since the last update.
   */
  update(dt: number): void {
    const step = Math.min(Math.max(dt, 0), MAX_STEP_SECONDS);
    this.#elapsed += step;

    this.grain.advance(this.#elapsed);
    this.#updateFocus(step);

    if (!this.autoBloom) return;

    if (this.#boostDecays && this.#boost > 0 && step > 0) {
      // Frame-rate independent exponential falloff.
      const next = this.#boost * Math.exp(-this.bloomDecay * step);
      this.#boost = next < BOOST_EPSILON ? 0 : next;
    }

    this.#applyBloom();
  }

  /**
   * Tell the chain how big the canvas is so it can pick a filter resolution
   * that respects the texture budget, and keep the grain the right apparent
   * size for the display.
   *
   * @param width - canvas width in CSS pixels.
   * @param height - canvas height in CSS pixels.
   * @param rendererResolution - the renderer's device pixel ratio.
   * @param designScale - `Stage.scale`, the design-unit to CSS-pixel ratio.
   */
  setScreen(
    width: number,
    height: number,
    rendererResolution: number,
    designScale = this.#designScale,
  ): void {
    this.grain.pixelRatio = rendererResolution;
    this.#rendererResolution = rendererResolution;
    this.#designScale = Math.max(1e-4, designScale);

    const w = Math.max(1, width);
    const h = Math.max(1, height);
    const budget = this.#maxTextureSize;
    const capped = Math.min(rendererResolution, budget / w, budget / h);

    // Only step down; never invent resolution the renderer is not providing.
    this.#resolutionCap = capped >= rendererResolution ? 'inherit' : Math.max(0.5, capped);
    this.#applyResolution();
    this.#applyBloomSpread();
    this.#applyDefocusBlur();
  }

  /**
   * Bloom blur radius in design units. A flare should be the same size relative
   * to the table whatever the window is doing, so this is the value worth
   * tuning — `bloom.spread` underneath it is in device pixels and is recomputed
   * on every resize.
   */
  get bloomSpread(): number {
    return this.#bloomSpreadDesign;
  }
  set bloomSpread(value: number) {
    this.#bloomSpreadDesign = Math.max(0, value);
    this.#applyBloomSpread();
  }

  /** Largest filter target edge in device pixels before the chain steps down. */
  get maxTextureSize(): number {
    return this.#maxTextureSize;
  }
  set maxTextureSize(value: number) {
    this.#maxTextureSize = Math.max(256, value);
  }

  /** Effective resolution of the chain, or `'inherit'` when uncapped. */
  get resolution(): number | 'inherit' {
    return this.#resolutionCap;
  }

  /**
   * Turn the whole chain on or off. Disabled filters are skipped by the filter
   * system rather than run as no-ops, so this is a real saving.
   */
  get enabled(): boolean {
    return this.filters.some((f) => f.enabled);
  }
  set enabled(value: boolean) {
    this.#chainEnabled = value;
    for (const filter of this.filters) filter.enabled = value;
    this.#syncShield();
    this.#syncGrade();
  }

  /**
   * Coarse quality preset, for weak GPUs or a reduced-motion preference.
   *
   * - `high`   full chain, three blur iterations
   * - `medium` full chain, two iterations at quarter resolution
   * - `low`    vignette, grain and the grade only — no multi-pass work at all
   *
   * Grain survives every preset: it is not decoration, it is what keeps the
   * large flat felt from banding. So does the grade: it is a single pass of
   * arithmetic with one texture read, and it is most of the look. What `low`
   * does drop is the defocus, which is the only part of the chain that
   * allocates extra targets mid-frame. This enables whatever it does not
   * explicitly disable, so set `enabled = false` afterwards if the chain is
   * meant to stay off entirely.
   */
  setQuality(quality: 'high' | 'medium' | 'low'): void {
    this.#chainEnabled = true;

    switch (quality) {
      case 'high':
        this.bloom.enabled = true;
        this.bloom.iterations = 3;
        this.bloom.downsample = 2;
        this.aberration.enabled = true;
        this.#defocusQuality = true;
        this.grade.blurDownsample = 2;
        break;
      case 'medium':
        this.bloom.enabled = true;
        this.bloom.iterations = 2;
        this.bloom.downsample = 4;
        this.aberration.enabled = true;
        this.#defocusQuality = true;
        this.grade.blurDownsample = 3;
        break;
      case 'low':
        this.bloom.enabled = false;
        this.aberration.enabled = false;
        this.#defocusQuality = false;
        break;
    }
    this.vignette.enabled = true;
    this.grain.enabled = true;
    if (!this.defocusEnabled) this.#killFocus();
    this.#syncShield();
    this.#syncGrade();
    // The downsample factor changed, so the pixel radius has to be rederived.
    this.#applyBloomSpread();
  }

  destroy(): void {
    for (const filter of this.filters) filter.destroy();
    this.filters.length = 0;
    this.shield.destroy();
    this.cardFilters.length = 0;
  }

  /** Resting level plus whatever the celebration layer is asking for. */
  #applyBloom(): void {
    this.bloom.intensity = this.bloomBase + this.#boost * this.bloomGain;
    this.#applyAberrationKick();
  }

  /**
   * The lens flinches with the flash.
   *
   * Driven from `#boost` rather than from an impulse of its own, deliberately.
   * A bonus already spikes the bloom on the same frame a streak would, and two
   * kicks with two decays racing each other do not read as one bigger event —
   * they read as a smear that outlasts its cause. Hanging this on the boost
   * that is already there means one impulse, one decay, and an aberration that
   * cannot drift out of step with the light that justifies it.
   *
   * The resting strength is untouched, so nothing changes when nothing is
   * happening: this only ever adds on top of it, and returns to exactly the
   * lens the frame is graded for. `aberrationGain` is small on purpose —
   * fringing is a thing you feel at the corners of a hit, not something you
   * should be able to sit and look at.
   */
  #applyAberrationKick(): void {
    this.aberration.strength = this.#aberrationBase + this.#boost * this.aberrationGain;
  }

  #applyResolution(): void {
    for (const filter of this.filters) filter.resolution = this.#resolutionCap;
    for (const filter of this.cardFilters) filter.resolution = this.#resolutionCap;
  }

  /** The capture only has a job while something downstream reads it. */
  #syncShield(): void {
    const needed = this.bloom.enabled || this.aberration.enabled;
    this.shield.capture.enabled = needed;
    if (!needed) this.shield.release();
  }

  /**
   * Design units -> pixels of the bloom's own (downsampled) target, which is
   * the space its blur offsets live in.
   */
  #applyBloomSpread(): void {
    const targetResolution = this.#filterResolution() / this.bloom.downsample;
    this.bloom.spread = this.#bloomSpreadDesign * this.#designScale * targetResolution;
  }

  /**
   * Design units -> device pixels of the incoming frame. The grade divides this
   * down itself for the pass that reads the reduced-resolution target, so a
   * defocus is the same physical softness at any window size or DPR.
   */
  #applyDefocusBlur(): void {
    this.grade.blurRadius = this.#defocusBlurDesign * this.#designScale * this.#filterResolution();
  }

  #filterResolution(): number {
    return this.#resolutionCap === 'inherit' ? this.#rendererResolution : this.#resolutionCap;
  }

  /**
   * Ease the focus ramp. Returns immediately — no easing, no uniform writes —
   * whenever nothing is focused, which is every frame of normal play.
   */
  #updateFocus(step: number): void {
    if (this.#focusTarget <= 0 && this.#focus <= 0) return;

    if (this.#focusHold > 0 && Number.isFinite(this.#focusHold)) {
      this.#focusHold -= step;
      if (this.#focusHold <= 0) {
        this.#focusHold = 0;
        this.#focusTarget = 0;
      }
    }

    // Frame-rate independent approach, quicker in than out.
    const rate = this.#focusTarget > this.#focus ? this.#focusAttack : this.#focusRelease;
    this.#focus += (this.#focusTarget - this.#focus) * (1 - Math.exp(-rate * step));
    if (this.#focusTarget <= 0 && this.#focus < FOCUS_EPSILON) this.#focus = 0;

    // Geometry always chases at the attack rate, so a second resolve landing
    // while the first is still fading glides across instead of cutting.
    const glide = 1 - Math.exp(-this.#focusAttack * step);
    this.#focusX += (this.#targetX - this.#focusX) * glide;
    this.#focusY += (this.#targetY - this.#focusY) * glide;
    this.#focusRadius += (this.#targetRadius - this.#focusRadius) * glide;

    this.#applyFocus();
  }

  /** Design units -> the filter's frame-relative units, then the enable gate. */
  #applyFocus(): void {
    // Radius and feather are in units of frame *height*, matching the
    // aspect-corrected distance the shader measures.
    const radius = this.#focusRadius / DESIGN.height;
    this.grade.setFocus(
      this.#focusX / DESIGN.width,
      this.#focusY / DESIGN.height,
      radius,
      radius * this.#focusFeather,
      this.#focus,
    );
    this.#syncGrade();
  }

  /** Cut the focus without an ease. Only for switching the effect off. */
  #killFocus(): void {
    this.#focus = 0;
    this.#focusTarget = 0;
    this.#focusHold = 0;
    this.#applyFocus();
  }

  /**
   * The grade is the last pass, and a pass that would be a straight copy is
   * worth not running at all: Pixi skips disabled filters outright rather than
   * ping-ponging through them.
   */
  #syncGrade(): void {
    this.grade.enabled =
      this.#chainEnabled && (this.grade.intensity > 0 || this.#focus > 0 || this.#focusTarget > 0);
  }
}

export function createPostFX(options?: PostFXOptions): PostFX {
  return new PostFX(options);
}
